/**
 * Резолв цели для `en.setVisible` / `setVisibleByLevel` (WUI-385).
 *
 * Паритет со Swing `JSContext`:
 * - `setVisible(level, Long id, visible)` → `getEntity(level, id)` + `Registry.IdFilter`
 * - `setVisible(level, String jsName, visible)` → по jsName / field name
 * - `setVisible(String name, visible)` → перебор registry по `getName()`
 *
 * Web registry (`GlobalProvider.elements`) индексируется по **field name**
 * (`hasWebsite`, `websiteUrlTable`), но скрипты платформы передают **XML id**
 * (`39114`, `39115`). Этот модуль сопоставляет id ↔ registry entry без смены ключа map.
 *
 * @see dev/WUI-385.md
 * @see swingui: JSContext.setVisible / getEntity / Registry.IdFilter
 */
import { actionIdsEqual } from '@/common/utils/resolveActionRegistryEntry'
import { findLayoutPageTabByVisibilityName } from '@/common/utils/pageTabMeta'

export type VisibilityTargetKind = 'element' | 'action' | 'panel' | 'overlay' | 'page'

/** Минимальный контракт записи registry, на которую можно вызвать setVisible. */
export interface VisibilityCapable {
  setVisible?: (visible: boolean) => void
  elementId?: unknown
  fieldName?: unknown
  formId?: unknown
  panelId?: unknown
  name?: unknown
  registryName?: unknown
  overlayId?: unknown
  data?: { name?: { _text?: unknown } }
}

/** Результат резолва: куда вызывать setVisible и какой registry key использовать для deferred/propagation. */
export interface VisibilityTarget {
  kind: VisibilityTargetKind
  registryKey: string
  elementId?: string | number
  setVisible: (visible: boolean) => void
}

export interface ResolveVisibilityTargetParams {
  level?: string
  id?: string | number
  jsName?: string
  name?: string
  formId?: string
  elements: Record<string, VisibilityCapable | undefined>
  actions: Record<string, VisibilityCapable | undefined>
  overlaysRegistry: Record<string, VisibilityCapable | undefined>
  panelsVisibilityRegistry: Record<
    string,
    | Array<(VisibilityCapable & { panelId?: string }) | undefined>
    | (VisibilityCapable & { panelId?: string })
    | undefined
  >
  /** Parsed form (or pageData) — PAGE tabs are not in `elements` registry. */
  layoutForm?: unknown
}

export function normalizeRegistryLevel(level: string | undefined | null): string {
  return String(level ?? '').trim().toLowerCase()
}

/** Platform XML id: только целое число (39115), не field name. */
export function parseNumericId(value: string | number | undefined | null): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const trimmed = String(value).trim()
  if (!/^\d+$/.test(trimmed)) return null
  const parsed = parseInt(trimmed, 10)
  return Number.isNaN(parsed) ? null : parsed
}

function matchesFormScope(entry: VisibilityCapable, formId?: string): boolean {
  if (!formId) return true
  const entryFormId = String(entry.formId ?? '').trim()
  if (!entryFormId) return true
  return entryFormId === String(formId).trim()
}

/**
 * Поиск зарегистрированного элемента по platform id из XML (`<element id="…">`).
 * Аналог Swing `getElement(long id)` + `Registry.IdFilter`.
 */
export function findRegisteredElementByElementId(
  elements: Record<string, VisibilityCapable | undefined>,
  elementId: string | number,
  formId?: string,
): { registryKey: string; entry: VisibilityCapable } | null {
  for (const [registryKey, entry] of Object.entries(elements)) {
    if (!entry || typeof entry.setVisible !== 'function') continue
    if (!actionIdsEqual(entry.elementId, elementId)) continue
    if (!matchesFormScope(entry, formId)) continue
    return { registryKey, entry }
  }
  return null
}

function entryMatchesRegistryName(entry: VisibilityCapable, name: string): boolean {
  const normalized = String(name ?? '').trim()
  if (!normalized) return false
  const fieldName = String(entry.fieldName ?? '').trim()
  if (fieldName && fieldName === normalized) return true
  const directName = String(entry.name ?? '').trim()
  if (directName && directName === normalized) return true
  return false
}

function registryKeyMatchesName(registryKey: string, normalizedName: string): boolean {
  if (registryKey === normalizedName) return true
  // Simple Label keys: `name#xmlId`
  if (registryKey.startsWith(`${normalizedName}#`)) return true
  return false
}

/**
 * Поиск по field name / registry key.
 * Аналог Swing `setVisible(String name, …)` — сравнение с `VisualEntity.getName()`.
 */
export function findRegisteredElementByName(
  elements: Record<string, VisibilityCapable | undefined>,
  name: string,
  formId?: string,
): { registryKey: string; entry: VisibilityCapable } | null {
  const normalized = String(name ?? '').trim()
  if (!normalized) return null

  const direct = elements[normalized]
  if (direct && typeof direct.setVisible === 'function' && matchesFormScope(direct, formId)) {
    return { registryKey: normalized, entry: direct }
  }

  for (const [registryKey, entry] of Object.entries(elements)) {
    if (!entry || typeof entry.setVisible !== 'function') continue
    if (!matchesFormScope(entry, formId)) continue
    if (
      registryKeyMatchesName(registryKey, normalized) ||
      entryMatchesRegistryName(entry, normalized)
    ) {
      return { registryKey, entry }
    }
  }

  return null
}

/** All registry entries whose key / field / name match — dialogClosed must update every one. */
export function findRegisteredElementsByName(
  elements: Record<string, VisibilityCapable | undefined>,
  name: string,
  formId?: string,
): Array<{ registryKey: string; entry: VisibilityCapable }> {
  const normalized = String(name ?? '').trim()
  if (!normalized) return []
  const seen = new Set<VisibilityCapable>()
  const out: Array<{ registryKey: string; entry: VisibilityCapable }> = []
  for (const [registryKey, entry] of Object.entries(elements)) {
    if (!entry || typeof entry.setVisible !== 'function') continue
    if (!matchesFormScope(entry, formId)) continue
    if (
      !registryKeyMatchesName(registryKey, normalized) &&
      !entryMatchesRegistryName(entry, normalized)
    ) {
      continue
    }
    if (seen.has(entry)) continue
    seen.add(entry)
    out.push({ registryKey, entry })
  }
  return out
}

/**
 * Like findRegisteredElementByName but does not require setVisible.
 * Used to resolve elementId for Label shell map when hooks were lost.
 */
export function findRegisteredElementByNameLoose(
  elements: Record<string, VisibilityCapable | undefined>,
  name: string,
  formId?: string,
): { registryKey: string; entry: VisibilityCapable } | null {
  const normalized = String(name ?? '').trim()
  if (!normalized) return null

  const direct = elements[normalized]
  if (direct && matchesFormScope(direct, formId)) {
    return { registryKey: normalized, entry: direct }
  }

  for (const [registryKey, entry] of Object.entries(elements)) {
    if (!entry) continue
    if (!matchesFormScope(entry, formId)) continue
    if (
      registryKeyMatchesName(registryKey, normalized) ||
      entryMatchesRegistryName(entry, normalized)
    ) {
      return { registryKey, entry }
    }
  }

  return null
}

function findRegisteredAction(
  actions: Record<string, VisibilityCapable | undefined>,
  id?: string | number,
  name?: string,
): { registryKey: string; entry: VisibilityCapable } | null {
  const numericId = id != null ? parseNumericId(id) : null
  if (numericId != null) {
    const direct = actions[String(numericId)] ?? actions[numericId]
    if (direct && typeof direct.setVisible === 'function') {
      return { registryKey: String(numericId), entry: direct }
    }
  }

  const normalizedName = String(name ?? (typeof id === 'string' ? id : '')).trim()
  if (!normalizedName) return null

  for (const [registryKey, entry] of Object.entries(actions)) {
    if (!entry || typeof entry.setVisible !== 'function') continue
    if (String(entry.registryName ?? '').trim() === normalizedName) {
      return { registryKey, entry }
    }
    if (String(entry.name ?? '').trim() === normalizedName) {
      return { registryKey, entry }
    }
    const dataName = String(entry.data?.name?._text ?? '').trim()
    if (dataName && dataName === normalizedName) {
      return { registryKey, entry }
    }
  }

  return null
}

function findRegisteredPanel(
  panelsVisibilityRegistry: ResolveVisibilityTargetParams['panelsVisibilityRegistry'],
  panelId: string | number,
): { registryKey: string; entry: VisibilityCapable } | null {
  const panelIdStr = String(panelId)
  for (const [registryKey, raw] of Object.entries(panelsVisibilityRegistry)) {
    const entries = Array.isArray(raw) ? raw : raw ? [raw] : []
    for (const entry of entries) {
      if (!entry || typeof entry.setVisible !== 'function') continue
      if (String(entry.panelId ?? '') === panelIdStr) {
        return { registryKey, entry }
      }
    }
  }
  return null
}

function findRegisteredOverlay(
  overlaysRegistry: Record<string, VisibilityCapable | undefined>,
  idOrName: string | number,
): { registryKey: string; entry: VisibilityCapable } | null {
  const idStr = String(idOrName)
  const direct = overlaysRegistry[idStr]
  if (direct && typeof direct.setVisible === 'function') {
    return { registryKey: idStr, entry: direct }
  }

  for (const [registryKey, entry] of Object.entries(overlaysRegistry)) {
    if (!entry || typeof entry.setVisible !== 'function') continue
    if (String(entry.overlayId ?? '') === idStr || String(entry.name ?? '') === idStr) {
      return { registryKey, entry }
    }
  }

  return null
}

function toVisibilityTarget(
  kind: VisibilityTargetKind,
  registryKey: string,
  entry: VisibilityCapable,
): VisibilityTarget | null {
  if (typeof entry.setVisible !== 'function') return null
  return {
    kind,
    registryKey,
    elementId: entry.elementId as string | number | undefined,
    setVisible: entry.setVisible.bind(entry),
  }
}

function layoutPageVisibilityTarget(
  layoutForm: unknown,
  name: string | number,
): VisibilityTarget | null {
  const match = findLayoutPageTabByVisibilityName(layoutForm, name)
  if (!match) return null
  const registryKey = match.id || match.jsName
  if (!registryKey) return null
  return {
    kind: 'page',
    registryKey,
    elementId: match.id || undefined,
    setVisible: () => {},
  }
}

/**
 * Единая точка резолва для `setVisibleByLevel` и двухаргументного `setVisible`.
 *
 * Ветки (как в Swing):
 * - без `level`, с `name` → element by name, затем numeric name → element id, action, overlay, page
 * - `level=element` + numeric `id` → `findRegisteredElementByElementId`
 * - `level=element` + string → field name / jsName
 * - `level=action|panel|overlaypage|page` → соответствующий registry / layout tabs
 */
export function resolveVisibilityTarget(
  params: ResolveVisibilityTargetParams,
): VisibilityTarget | null {
  const {
    level,
    id,
    jsName,
    name,
    formId,
    elements,
    actions,
    overlaysRegistry,
    panelsVisibilityRegistry,
    layoutForm,
  } = params

  const levelNorm = normalizeRegistryLevel(level)
  const nameArg = String(name ?? '').trim()

  if (!levelNorm && nameArg) {
    const byName = findRegisteredElementByName(elements, nameArg, formId)
    if (byName) return toVisibilityTarget('element', byName.registryKey, byName.entry)

    const numericName = parseNumericId(nameArg)
    if (numericName != null) {
      const byId = findRegisteredElementByElementId(elements, numericName, formId)
      if (byId) return toVisibilityTarget('element', byId.registryKey, byId.entry)
    }

    const action = findRegisteredAction(actions, undefined, nameArg)
    if (action) return toVisibilityTarget('action', action.registryKey, action.entry)

    const overlay = findRegisteredOverlay(overlaysRegistry, nameArg)
    if (overlay) return toVisibilityTarget('overlay', overlay.registryKey, overlay.entry)

    return layoutPageVisibilityTarget(layoutForm, nameArg)
  }

  const lookupId = id ?? jsName
  if (lookupId == null || lookupId === '') return null

  if (levelNorm === 'overlaypage' || levelNorm === 'overlay') {
    const overlay = findRegisteredOverlay(overlaysRegistry, lookupId)
    if (overlay) return toVisibilityTarget('overlay', overlay.registryKey, overlay.entry)
    return null
  }

  if (levelNorm === 'action') {
    const action = findRegisteredAction(actions, lookupId, jsName)
    if (action) return toVisibilityTarget('action', action.registryKey, action.entry)
    return null
  }

  if (levelNorm === 'panel') {
    const panel = findRegisteredPanel(panelsVisibilityRegistry, lookupId)
    if (panel) return toVisibilityTarget('panel', panel.registryKey, panel.entry)
    return null
  }

  if (levelNorm === 'element' || levelNorm === '') {
    const numericId = parseNumericId(lookupId)
    if (numericId != null) {
      const byId = findRegisteredElementByElementId(elements, numericId, formId)
      if (byId) return toVisibilityTarget('element', byId.registryKey, byId.entry)
    }

    const byName = findRegisteredElementByName(
      elements,
      String(jsName ?? lookupId),
      formId,
    )
    if (byName) return toVisibilityTarget('element', byName.registryKey, byName.entry)

    const action = findRegisteredAction(actions, lookupId, jsName)
    if (action) return toVisibilityTarget('action', action.registryKey, action.entry)

    return null
  }

  if (levelNorm === 'page') {
    const pageKey = String(lookupId)
    const pageEntry = elements[pageKey]
    if (pageEntry && typeof pageEntry.setVisible === 'function') {
      return toVisibilityTarget('page', pageKey, pageEntry)
    }
    return layoutPageVisibilityTarget(layoutForm, lookupId)
  }

  return null
}
